/**
 * UploadDocumentDialog — matter "Upload additional documents" (LF-4885).
 *
 * Drop surface is the shared `FileDropZone`; each queued file renders through
 * the shared `FileRow`, with its per-file "certified copy" toggle in the row's
 * `footer` slot (inside the card). Extraction is a batch-level choice made at
 * submit time via two footer actions (VALE-757), applied to the whole batch by
 * the caller's upload handler.
 * State/options/upload stay caller-owned on `useFileUpload` (which already
 * drives real per-file progress via its `onUpload` helpers), so files upload on
 * submit, not on drop.
 *
 * When the matter can offer one, a second source — "Import from {provider}" —
 * swaps this same dialog's body to the Extract wizard's own PMS picker
 * (`SmokeballSearchStep`, VALE-352). There is deliberately only one picker
 * implementation: this dialog and the Extract wizard both drive it, so the
 * multi-select / already-in-Vale behaviour can never drift between the two
 * entry points. Import lands each selected file via the unified
 * `documents/attach` endpoint (`source: 'pms'`) — the same synchronous,
 * manual-upload-equivalent landing the Redemption "Attach from Smokeball" flow
 * uses — rather than the Temporal migration workflow Extract's button starts,
 * because that workflow always types the file `UNKNOWN_PENDING_AI_ANALYSIS`
 * pending a mandatory AI pass; this dialog's files must honour the "Analyse
 * with Vale AI" opt-in instead, landing as an ordinary `ADDITIONAL_DOCUMENT`
 * when it's unchecked.
 */

'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeft } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  FILE_DROP_ZONE_DRAG_COPY,
  FileDropZone,
  FileRow,
  type UploadFile
} from '@/components/ui/file-drop-zone';
import { useDragAndDrop } from '@/hooks/use-drag-and-drop';
import { getFileTypeDescriptor, formatFileSize } from '@/lib/services/files';
import { useFileUpload, type FileUploadHelpers } from '@/hooks/use-file-upload';
import {
  ADDITIONAL_DOCUMENT_ACCEPTED_FORMATS_HINT,
  FILE_UPLOAD_CONSTRAINTS
} from '@/lib/config/document-types';
import { toast } from '@/lib/toast';
import { FileTypeSlug } from '@/lib/services/files/file-types';
import { SmokeballSearchStep } from '@/components/documents/ai-import/smokeball-search-step';
import { attachMatterDocument } from '@/lib/services/redemption/pms-import';
import { cn } from '@/lib/utils';

const MAX_FILE_SIZE_MB = Math.round(
  FILE_UPLOAD_CONSTRAINTS.maxFileSize / (1024 * 1024)
);

export interface UploadDocumentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUpload: (
    files: Array<{
      file: File;
      fileType: string;
      isCertifiedCopy: boolean;
      analyzeWithAI?: boolean;
    }>,
    /** Real per-file progress + abort (LF-4771). Callers may ignore it. */
    helpers: FileUploadHelpers
  ) => Promise<void>;
  maxFiles?: number;
  /**
   * Files to stage the moment the dialog opens, for a caller that already has
   * them — a page that accepts a drop and wants the reader to land here with the
   * file in hand rather than dropping it a second time (VALE-404).
   *
   * They go through the same validation as a manual selection, so an oversized
   * or wrong-typed drop is rejected here with the usual toast rather than being
   * trusted because it came from a caller.
   */
  initialFiles?: File[];
  /** Required to offer the PMS import source — the picker searches this matter's PMS documents. */
  matterId?: number;
  /**
   * The matter's PMS provider name (e.g. "Actionstep"), or `undefined` when the
   * matter can't offer PMS import (unsynced, no active integration, a provider
   * without inbound document legs) — see `canImportDocumentsFromPms`. Omit or
   * pass `undefined` to hide the source entirely.
   */
  pmsProviderLabel?: string;
  /**
   * Fired after a PMS import batch lands, with the files that landed
   * successfully and whether extraction was opted into. The caller
   * decides what analysis / cache-invalidation that implies, exactly as it
   * already does for `onUpload`.
   */
  onPmsImportComplete?: (
    files: Array<{ fileId: number; fileName: string }>,
    analyzeWithAI: boolean
  ) => Promise<void> | void;
}

type DialogView = 'upload' | 'pms_search' | 'pms_importing';

export function UploadDocumentDialog({
  open,
  onOpenChange,
  onUpload,
  maxFiles,
  initialFiles,
  matterId,
  pmsProviderLabel,
  onPmsImportComplete
}: UploadDocumentDialogProps) {
  /**
   * Batch-level extraction choice, applied to every file by the caller's upload
   * handler (VALE-757).
   *
   * The manual-upload view makes the choice at click time via two footer
   * actions, so it is parked in a ref that `useFileUpload`'s callback reads —
   * a `useState` set in the same tick would not be visible to it.
   *
   * The PMS view keeps a checkbox: its import fires the moment files are picked
   * in `SmokeballSearchStep`, so there is no footer button to hang a second
   * action off. Both default to not extracting.
   */
  const extractChoiceRef = useRef(false);
  const [pmsAnalyzeWithAI, setPmsAnalyzeWithAI] = useState(false);
  const [view, setView] = useState<DialogView>('upload');
  const [pmsError, setPmsError] = useState<string | undefined>(undefined);

  const {
    files,
    addFiles,
    removeFile,
    clearFiles,
    uploadFiles,
    isUploading,
    updateCertifiedCopy,
    validateFiles,
    hasFiles,
    canUpload
  } = useFileUpload({
    maxFiles,
    maxFileSize: FILE_UPLOAD_CONSTRAINTS.maxFileSize,
    // All uploads through this dialog land as additional documents.
    autoDetectFileType: false,
    onUpload: async (uploadFiles, helpers) => {
      const filesToUpload = uploadFiles.map((f) => ({
        file: f.file,
        fileType: FileTypeSlug.ADDITIONAL_DOCUMENT,
        isCertifiedCopy: f.isCertifiedCopy || false,
        analyzeWithAI: extractChoiceRef.current
      }));
      await onUpload(filesToUpload, helpers);
    }
  });

  const handleFilesSelected = useCallback(
    (newFiles: File[]) => {
      const { valid, invalid } = validateFiles(newFiles);
      if (invalid.length > 0) {
        invalid.forEach(({ file, error }) =>
          toast.error(`${file.name}: ${error}`)
        );
      }
      if (valid.length > 0) addFiles(valid);
    },
    [validateFiles, addFiles]
  );

  const isPmsView = view !== 'upload';

  /**
   * THE WHOLE DIALOG IS THE DROP TARGET (VALE-1592).
   *
   * The `FileDropZone` strip below is a few lines tall inside a 600px dialog, and the thing
   * a reader aims a dragged file at is the dialog. Before this, a release over the title,
   * the queued-file list or the footer hit no drop target at all and the browser navigated
   * the tab to the file, taking the dialog and everything staged in it. `StrayFileDropGuard`
   * now stops the navigation everywhere in the app; this is the other half — the miss lands
   * where the reader meant it to.
   *
   * **The handlers go on in the CAPTURE phase**, which is the load-bearing detail. The strip
   * `stopPropagation`s its own drag events, so bubble-phase handlers out here would see an
   * unbalanced enter/leave stream — the dialog-wide affordance would blink off the moment
   * the cursor crossed the strip, which is precisely the "only the strip is real" impression
   * this change exists to remove. Capturing first, and stopping there, makes the dialog the
   * single drop target: one counter, one overlay, and the strip left to do what it is
   * actually good for, which is click-to-browse.
   *
   * `filesOnly` keeps every other drag inside the dialog alone — a checkbox, a button, text
   * dragged between fields — since an area this large will cover gestures that have nothing
   * to do with uploading.
   */
  const { isDragging: isDialogDragging, getRootProps: getDialogDragProps } =
    useDragAndDrop({
      onDrop: handleFilesSelected,
      multiple: true,
      filesOnly: true,
      // While a batch is uploading, or on the PMS views, the dialog still swallows the drop
      // (the handlers preventDefault before returning) — it just does not queue it.
      disabled: isUploading || isPmsView
    });
  const dialogDragProps = getDialogDragProps();

  /**
   * Stage a caller's dropped files once per opening.
   *
   * Keyed on `open` going true rather than on `initialFiles` changing: the
   * caller hands over a fresh array, and re-running on identity would re-add the
   * same files every render. Clearing the ref on close is what lets a second
   * drop stage again.
   */
  const seededRef = useRef(false);
  useEffect(() => {
    if (!open) {
      seededRef.current = false;
      return;
    }
    if (seededRef.current) return;
    seededRef.current = true;
    if (initialFiles?.length) handleFilesSelected(initialFiles);
  }, [open, initialFiles, handleFilesSelected]);

  const handleUpload = async (extract: boolean) => {
    extractChoiceRef.current = extract;
    try {
      await uploadFiles();
      onOpenChange(false);
      clearFiles();
      extractChoiceRef.current = false;
      // Success toast is handled by the upload mutation hook.
    } catch (error) {
      // Error toast is handled by the upload mutation hook.
      console.error('Upload failed:', error);
    }
  };

  const isPmsImporting = view === 'pms_importing';

  const handleClose = () => {
    if (!isUploading && !isPmsImporting) {
      onOpenChange(false);
      clearFiles();
      extractChoiceRef.current = false;
      setPmsAnalyzeWithAI(false);
      setView('upload');
      setPmsError(undefined);
    }
  };

  const handlePmsFilesSelected = async (
    selected: Array<{ externalId: string; fileName: string }>
  ) => {
    if (matterId == null) return;
    setPmsError(undefined);
    setView('pms_importing');

    const landed: Array<{ fileId: number; fileName: string }> = [];
    for (const file of selected) {
      try {
        const result = await attachMatterDocument(matterId, {
          source: 'pms',
          externalId: file.externalId,
          fileName: file.fileName,
          fileType: 'additional-document'
        });
        landed.push({ fileId: result.fileId, fileName: file.fileName });
      } catch (error) {
        toast.error(
          `${file.fileName}: ${error instanceof Error ? error.message : 'Failed to import'}`
        );
      }
    }

    if (landed.length === 0) {
      setView('pms_search');
      setPmsError('None of the selected documents could be imported.');
      return;
    }

    toast.success(
      `Imported ${landed.length} document${landed.length > 1 ? 's' : ''} from ${pmsProviderLabel}`
    );
    await onPmsImportComplete?.(landed, pmsAnalyzeWithAI);
    onOpenChange(false);
    clearFiles();
    setPmsAnalyzeWithAI(false);
    setView('upload');
  };

  const handleBackToUpload = () => {
    setPmsError(undefined);
    setView('upload');
  };

  // Per-file row: shared FileRow + a certified-copy toggle in its footer (inside
  // the card). The toggle only shows while the file is still queued.
  const renderRow = useCallback(
    (f: UploadFile) => {
      const showCertified = f.status !== 'uploading' && f.status !== 'success';
      return (
        <li key={f.id}>
          <FileRow
            label={getFileTypeDescriptor(f.file.type, f.file.name).label}
            name={f.file.name}
            status={f.status === 'pending' ? 'idle' : f.status}
            sizeLabel={formatFileSize(f.file.size)}
            progress={f.status === 'uploading' ? f.progress : undefined}
            error={f.error}
            onRemove={isUploading ? undefined : () => removeFile(f.id)}
            footer={
              showCertified ? (
                <div className="flex items-center gap-2">
                  <Checkbox
                    id={`certified-${f.id}`}
                    checked={f.isCertifiedCopy || false}
                    onCheckedChange={(checked) =>
                      updateCertifiedCopy(f.id, checked === true)
                    }
                    disabled={isUploading}
                  />
                  <Label
                    htmlFor={`certified-${f.id}`}
                    className="cursor-pointer text-sm font-normal"
                  >
                    This is a certified copy
                  </Label>
                </div>
              ) : undefined
            }
          />
        </li>
      );
    },
    [isUploading, removeFile, updateCertifiedCopy]
  );

  const canOfferPmsImport =
    view === 'upload' && !hasFiles && matterId != null && !!pmsProviderLabel;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        onDragEnterCapture={dialogDragProps.onDragEnter}
        onDragLeaveCapture={dialogDragProps.onDragLeave}
        onDragOverCapture={dialogDragProps.onDragOver}
        onDropCapture={dialogDragProps.onDrop}
        className={cn(
          'sm:max-w-[600px]',
          // The PMS picker (SmokeballSearchStep, VALE-352) needs room for its
          // checkbox/name/size/date columns — matches the width the Extract
          // wizard already uses for the same shared component (VALE-406).
          view === 'pms_search' && 'sm:max-w-4xl'
        )}
      >
        <DialogHeader className="flex flex-row items-center">
          <div>
            <DialogTitle>
              {view === 'upload'
                ? 'Upload Additional Documents'
                : `Import from ${pmsProviderLabel}`}
            </DialogTitle>
            <DialogDescription>
              {view === 'upload'
                ? `Add supporting documents to this matter.${
                    maxFiles
                      ? ` You can upload up to ${maxFiles} files at once.`
                      : ''
                  }`
                : `Select documents to import from ${pmsProviderLabel}.`}
            </DialogDescription>
          </div>
          {view !== 'upload' && (
            <Button
              className="ms-auto"
              variant="outline"
              size="sm"
              onClick={handleBackToUpload}
              disabled={isPmsImporting}
            >
              <ChevronLeft className="mr-2 h-4 w-4" />
              Back
            </Button>
          )}
        </DialogHeader>

        {view === 'upload' && (
          <>
            <div className="min-w-0 space-y-4">
              <FileDropZone
                variant="compact"
                multiple
                accept={FILE_UPLOAD_CONSTRAINTS.accept}
                maxSizeBytes={FILE_UPLOAD_CONSTRAINTS.maxFileSize}
                hintText={`${ADDITIONAL_DOCUMENT_ACCEPTED_FORMATS_HINT}, up to ${MAX_FILE_SIZE_MB} MB`}
                disabled={isUploading}
                onFilesAdded={handleFilesSelected}
              />

              {canOfferPmsImport && (
                <Button
                  pad="lg"
                  variant="outline"
                  size="xs"
                  type="button"
                  onClick={() => setView('pms_search')}
                >
                  + Import from {pmsProviderLabel}
                </Button>
              )}

              {hasFiles && (
                <ScrollArea className="h-[340px] pr-4">
                  <ul className="space-y-3">{files.map(renderRow)}</ul>
                </ScrollArea>
              )}
            </div>

            {hasFiles && (
              <p className="text-muted-foreground border-t pt-3 text-xs">
                Extracting reads these documents and proposes contacts, assets
                and beneficiaries for you to review. You can also run it later
                from the Documents page.
              </p>
            )}

            <DialogFooter>
              <Button
                variant="outline"
                onClick={handleClose}
                disabled={isUploading}
              >
                Cancel
              </Button>
              {hasFiles && (
                <Button
                  size="xs"
                  density="compact"
                  variant="outline"
                  onClick={() => handleUpload(false)}
                  disabled={!canUpload}
                >
                  {isUploading
                    ? 'Uploading...'
                    : `Upload ${files.length} file${files.length > 1 ? 's' : ''}`}
                </Button>
              )}
              {hasFiles && (
                <Button
                  size="xs"
                  density="compact"
                  onClick={() => handleUpload(true)}
                  disabled={!canUpload}
                >
                  {isUploading ? 'Uploading...' : 'Upload & extract'}
                </Button>
              )}
            </DialogFooter>
          </>
        )}

        {view === 'pms_search' && matterId != null && (
          <>
            <div className="flex items-center gap-2 border-b pb-3">
              <Checkbox
                id="analyze-with-ai-pms"
                checked={pmsAnalyzeWithAI}
                onCheckedChange={(checked) =>
                  setPmsAnalyzeWithAI(checked === true)
                }
              />
              <Label
                htmlFor="analyze-with-ai-pms"
                className="text-muted-foreground cursor-pointer text-sm font-normal"
              >
                Also extract contacts, assets and beneficiaries from these
                documents for review
              </Label>
            </div>
            <SmokeballSearchStep
              matterId={matterId}
              onFilesSelected={handlePmsFilesSelected}
              error={pmsError}
            />
          </>
        )}

        {view === 'pms_importing' && (
          <div className="rounded-lg border border-dashed p-12 text-center">
            <p className="text-muted-foreground text-sm">
              Importing documents from {pmsProviderLabel}…
            </p>
          </div>
        )}

        {/* The drag-active affordance for the whole target, wearing `FileDropZone`'s own
            drag treatment (violet dashed border and ring) and its own words, so dropping
            on the dialog and dropping on the strip cannot read as two different things.
            `pointer-events-none` is load-bearing: an element appearing under the cursor
            mid-drag would take the next `dragenter` and emit no matching `dragleave`,
            pinning the counter above zero so the overlay never dismissed.

            The ground is the dialog's own surface token, fully opaque, not a violet wash. The
            strip underneath carries its own copy ("Drag & drop or click to upload") and a
            Browse button, and a 5%-tint overlay left both legible straight through the
            overlay's words — two sets of text in the same box. `FileDropZoneSurface` does
            not hit this because it hides its content (`invisible`) while dragging; an
            overlay spanning a whole dialog cannot, so it has to mask instead. */}
        {isDialogDragging && (
          <div
            aria-hidden="true"
            data-testid="upload-dialog-drag-overlay"
            className="border-ai bg-popover ring-ai/25 pointer-events-none absolute inset-0 z-20 flex flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed px-4 text-center ring-1"
          >
            <p className="text-ai-subtle-foreground text-sm font-semibold">
              {FILE_DROP_ZONE_DRAG_COPY.title}
            </p>
            <p className="text-muted-foreground text-xs">
              {FILE_DROP_ZONE_DRAG_COPY.subtext}
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
